import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { Briefcase, Plus, Edit, Trash2, MapPin, Phone, Mail, Globe, Clock, Tag, FileText, X, Save } from 'lucide-react';

const Fornecedores = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [fornecedores, setFornecedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFornecedor, setEditingFornecedor] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    pessoa_tipo_pessoa: 'PJ',
    pessoa_nome: '',
    pessoa_cpf: '',
    pessoa_cnpj: '',
    pessoa_razao_social: '',
    pessoa_nome_fantasia: '',
    pessoa_inscricao_estadual: '',
    pessoa_inscricao_municipal: '',
    pessoa_email: '',
    pessoa_telefone: '',
    pessoa_categoria_fornecedor: '',
    pessoa_servicos_oferecidos: '',
    pessoa_horario_atendimento: '',
    pessoa_site: '',
    pessoa_observacoes: '',
    condominio_id: getCondominioId(),
    pessoa_ativo: true
  });

  // Categorias de fornecedores predefinidas
  const categorias = [
    'Encanador',
    'Eletricista',
    'Pintor',
    'Pedreiro',
    'Jardineiro',
    'Marceneiro',
    'Vidraceiro',
    'Dedetizador',
    'Limpeza',
    'Segurança',
    'Manutenção',
    'Ar Condicionado',
    'Elevadores',
    'Portaria',
    'Outros'
  ];

  useEffect(() => {
    const condominioId = getCondominioId();
    if (condominioId) {
      loadFornecedores();
    }
  }, [condominioSelecionado]);

  const loadFornecedores = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) {
        console.log('Condomínio não selecionado');
        return;
      }
      
      const response = await api.get('/fornecedores', {
        params: { condominio_id: condominioId }
      });
      
      setFornecedores(response.data.data.fornecedores || []);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
      setFornecedores([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);
    
    try {
      const dadosParaEnvio = {
        ...formData,
        condominio_id: getCondominioId(),
        pessoa_cpf: formData.pessoa_tipo_pessoa === 'PF' ? formData.pessoa_cpf : null,
        pessoa_cnpj: formData.pessoa_tipo_pessoa === 'PJ' ? formData.pessoa_cnpj : null,
        pessoa_razao_social: formData.pessoa_tipo_pessoa === 'PJ' ? formData.pessoa_razao_social : null,
        pessoa_nome_fantasia: formData.pessoa_tipo_pessoa === 'PJ' ? formData.pessoa_nome_fantasia : null,
        pessoa_inscricao_estadual: formData.pessoa_tipo_pessoa === 'PJ' ? formData.pessoa_inscricao_estadual : null,
        pessoa_inscricao_municipal: formData.pessoa_tipo_pessoa === 'PJ' ? formData.pessoa_inscricao_municipal : null
      };

      if (editingFornecedor) {
        await api.put(`/fornecedores/${editingFornecedor.pessoa_id || editingFornecedor.id}`, dadosParaEnvio);
      } else {
        await api.post('/fornecedores', dadosParaEnvio);
      }
      
      setShowModal(false);
      setEditingFornecedor(null);
      resetForm();
      loadFornecedores();
      
      if (window.showNotification) {
        window.showNotification(
          editingFornecedor ? 'Fornecedor atualizado com sucesso!' : 'Fornecedor criado com sucesso!',
          'success'
        );
      }
    } catch (error) {
      console.error('Erro ao salvar fornecedor:', error);
      
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        setErrorMessage(errorMessages);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (fornecedor) => {
    setEditingFornecedor(fornecedor);
    setFormData({
      pessoa_tipo_pessoa: fornecedor.pessoa_tipo_pessoa || fornecedor.tipo_pessoa || 'PJ',
      pessoa_nome: fornecedor.pessoa_nome || fornecedor.nome || '',
      pessoa_cpf: fornecedor.pessoa_cpf || fornecedor.cpf || '',
      pessoa_cnpj: fornecedor.pessoa_cnpj || fornecedor.cnpj || '',
      pessoa_razao_social: fornecedor.pessoa_razao_social || fornecedor.razao_social || '',
      pessoa_nome_fantasia: fornecedor.pessoa_nome_fantasia || fornecedor.nome_fantasia || '',
      pessoa_inscricao_estadual: fornecedor.pessoa_inscricao_estadual || '',
      pessoa_inscricao_municipal: fornecedor.pessoa_inscricao_municipal || '',
      pessoa_email: fornecedor.pessoa_email || fornecedor.email || '',
      pessoa_telefone: fornecedor.pessoa_telefone || fornecedor.telefone || '',
      pessoa_categoria_fornecedor: fornecedor.pessoa_categoria_fornecedor || fornecedor.fornecedor_categoria || '',
      pessoa_servicos_oferecidos: fornecedor.pessoa_servicos_oferecidos || fornecedor.fornecedor_servicos || '',
      pessoa_horario_atendimento: fornecedor.pessoa_horario_atendimento || fornecedor.fornecedor_horario || '',
      pessoa_site: fornecedor.pessoa_site || fornecedor.fornecedor_site || '',
      pessoa_observacoes: fornecedor.pessoa_observacoes || '',
      condominio_id: getCondominioId(),
      pessoa_ativo: fornecedor.pessoa_ativo !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este fornecedor?')) {
      try {
        await api.delete(`/fornecedores/${id}`);
        loadFornecedores();
        
        if (window.showNotification) {
          window.showNotification('Fornecedor excluído com sucesso!', 'success');
        }
      } catch (error) {
        console.error('Erro ao excluir fornecedor:', error);
        if (window.showNotification) {
          window.showNotification('Erro ao excluir fornecedor', 'error');
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      pessoa_tipo_pessoa: 'PJ',
      pessoa_nome: '',
      pessoa_cpf: '',
      pessoa_cnpj: '',
      pessoa_razao_social: '',
      pessoa_nome_fantasia: '',
      pessoa_inscricao_estadual: '',
      pessoa_inscricao_municipal: '',
      pessoa_email: '',
      pessoa_telefone: '',
      pessoa_categoria_fornecedor: '',
      pessoa_servicos_oferecidos: '',
      pessoa_horario_atendimento: '',
      pessoa_site: '',
      pessoa_observacoes: '',
      condominio_id: getCondominioId(),
      pessoa_ativo: true
    });
    setErrorMessage('');
  };

  // Função para aplicar máscara de CPF
  const applyCpfMask = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return numbers.replace(/^(\d{3})(\d+)/, '$1.$2');
    } else if (numbers.length <= 9) {
      return numbers.replace(/^(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
    } else {
      return numbers.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
  };

  // Função para aplicar máscara de CNPJ
  const applyCnpjMask = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 5) {
      return numbers.replace(/^(\d{2})(\d+)/, '$1.$2');
    } else if (numbers.length <= 8) {
      return numbers.replace(/^(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
    } else if (numbers.length <= 12) {
      return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
    } else {
      return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  // Função para aplicar máscara de telefone
  const applyPhoneMask = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 6) {
      return numbers.replace(/^(\d{2})(\d+)/, '($1) $2');
    } else if (numbers.length <= 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d+)/, '($1) $2-$3');
    } else {
      return numbers.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;

    // Aplicar máscaras
    if (name === 'pessoa_cpf') {
      processedValue = applyCpfMask(value);
    } else if (name === 'pessoa_cnpj') {
      processedValue = applyCnpjMask(value);
    } else if (name === 'pessoa_telefone') {
      processedValue = applyPhoneMask(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
  };

  const getTipoPessoaLabel = (tipo) => {
    return tipo === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica';
  };

  const formatCpfCnpj = (tipo, cpf, cnpj) => {
    if (tipo === 'PF' && cpf) {
      return applyCpfMask(cpf);
    }
    if (tipo === 'PJ' && cnpj) {
      return applyCnpjMask(cnpj);
    }
    return '-';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Briefcase className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Fornecedores</h1>
        </div>
        {(['sindico', 'subsindico', 'administrador'].includes(user?.tipo) && getCondominioId()) ? (
          <button
            onClick={() => {
              setEditingFornecedor(null);
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Novo Fornecedor
          </button>
        ) : isAdministrador() && !getCondominioId() ? (
          <div className="text-sm text-gray-500">
            Selecione um condomínio para gerenciar fornecedores
          </div>
        ) : null}
      </div>

      {!getCondominioId() && isAdministrador() ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <MapPin className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Selecione um condomínio
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Para visualizar e gerenciar fornecedores, selecione um condomínio no menu superior.</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Lista de Fornecedores */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="table-container">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome/Razão Social
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CPF/CNPJ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Telefone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fornecedores.map((fornecedor) => (
                    <tr key={fornecedor.pessoa_id || fornecedor.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Briefcase className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {fornecedor.pessoa_nome || fornecedor.nome}
                            </div>
                            {fornecedor.pessoa_nome_fantasia && (
                              <div className="text-sm text-gray-500">
                                {fornecedor.pessoa_nome_fantasia}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          {getTipoPessoaLabel(fornecedor.pessoa_tipo_pessoa || fornecedor.tipo_pessoa)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCpfCnpj(
                          fornecedor.pessoa_tipo_pessoa || fornecedor.tipo_pessoa,
                          fornecedor.pessoa_cpf || fornecedor.cpf,
                          fornecedor.pessoa_cnpj || fornecedor.cnpj
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {fornecedor.pessoa_categoria_fornecedor || fornecedor.fornecedor_categoria || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {fornecedor.pessoa_telefone || fornecedor.telefone || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {fornecedor.pessoa_email || fornecedor.email || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          fornecedor.pessoa_ativo || fornecedor.ativo
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {(fornecedor.pessoa_ativo || fornecedor.ativo) ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(fornecedor)}
                            className="p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {['sindico', 'administrador'].includes(user?.tipo) && (
                            <button
                              onClick={() => handleDelete(fornecedor.pessoa_id || fornecedor.id)}
                              className="p-2 bg-red-100 text-red-600 hover:bg-red-200 rounded"
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container custom-scrollbar">
            <div className="modal-header">
              <h3 className="modal-title">
                {editingFornecedor ? 'Editar Fornecedor' : 'Novo Fornecedor'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingFornecedor(null);
                  resetForm();
                }}
                className="modal-close-btn"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {errorMessage && (
              <div className="alert alert-error">
                <svg className="alert-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="alert-content">
                  <h3 className="alert-title">Erro ao salvar fornecedor</h3>
                  <p className="alert-message">{errorMessage}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Tipo de Pessoa */}
              <div className="form-section">
                <h4 className="form-section-title">Tipo de Pessoa</h4>
                <div className="form-grid form-grid-2">
                  <div className="form-field form-field-full">
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="pessoa_tipo_pessoa"
                          value="PF"
                          checked={formData.pessoa_tipo_pessoa === 'PF'}
                          onChange={handleInputChange}
                          className="form-radio"
                        />
                        <span className="ml-2 text-sm text-gray-700">Pessoa Física</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="pessoa_tipo_pessoa"
                          value="PJ"
                          checked={formData.pessoa_tipo_pessoa === 'PJ'}
                          onChange={handleInputChange}
                          className="form-radio"
                        />
                        <span className="ml-2 text-sm text-gray-700">Pessoa Jurídica</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dados da Pessoa Física */}
              {formData.pessoa_tipo_pessoa === 'PF' && (
                <div className="form-section">
                  <h4 className="form-section-title">Dados Pessoais</h4>
                  <div className="form-grid form-grid-2">
                    <div className="form-field">
                      <label className="form-label form-label-required">Nome Completo</label>
                      <input
                        type="text"
                        name="pessoa_nome"
                        value={formData.pessoa_nome}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                        placeholder="Digite o nome completo"
                      />
                    </div>

                    <div className="form-field">
                      <label className="form-label form-label-required">CPF</label>
                      <input
                        type="text"
                        name="pessoa_cpf"
                        value={formData.pessoa_cpf}
                        onChange={handleInputChange}
                        required={formData.pessoa_tipo_pessoa === 'PF'}
                        maxLength="14"
                        placeholder="000.000.000-00"
                        className="form-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Dados da Pessoa Jurídica */}
              {formData.pessoa_tipo_pessoa === 'PJ' && (
                <div className="form-section">
                  <h4 className="form-section-title">Dados da Empresa</h4>
                  <div className="form-grid form-grid-2">
                    <div className="form-field">
                      <label className="form-label form-label-required">Razão Social</label>
                      <input
                        type="text"
                        name="pessoa_razao_social"
                        value={formData.pessoa_razao_social}
                        onChange={handleInputChange}
                        required={formData.pessoa_tipo_pessoa === 'PJ'}
                        className="form-input"
                        placeholder="Digite a razão social"
                      />
                    </div>

                    <div className="form-field">
                      <label className="form-label">Nome Fantasia</label>
                      <input
                        type="text"
                        name="pessoa_nome_fantasia"
                        value={formData.pessoa_nome_fantasia}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Digite o nome fantasia"
                      />
                    </div>

                    <div className="form-field">
                      <label className="form-label form-label-required">CNPJ</label>
                      <input
                        type="text"
                        name="pessoa_cnpj"
                        value={formData.pessoa_cnpj}
                        onChange={handleInputChange}
                        required={formData.pessoa_tipo_pessoa === 'PJ'}
                        maxLength="18"
                        placeholder="00.000.000/0000-00"
                        className="form-input"
                      />
                    </div>

                    <div className="form-field">
                      <label className="form-label">Inscrição Estadual</label>
                      <input
                        type="text"
                        name="pessoa_inscricao_estadual"
                        value={formData.pessoa_inscricao_estadual}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Digite a inscrição estadual"
                      />
                    </div>

                    <div className="form-field">
                      <label className="form-label">Inscrição Municipal</label>
                      <input
                        type="text"
                        name="pessoa_inscricao_municipal"
                        value={formData.pessoa_inscricao_municipal}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Digite a inscrição municipal"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Dados de Contato */}
              <div className="form-section">
                <h4 className="form-section-title">Dados de Contato</h4>
                <div className="form-grid form-grid-2">
                  <div className="form-field">
                    <label className="form-label form-label-required">Telefone</label>
                    <input
                      type="text"
                      name="pessoa_telefone"
                      value={formData.pessoa_telefone}
                      onChange={handleInputChange}
                      required
                      maxLength="15"
                      placeholder="(11) 99999-9999"
                      className="form-input"
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="pessoa_email"
                      value={formData.pessoa_email}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="email@exemplo.com"
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-label">Site</label>
                    <input
                      type="url"
                      name="pessoa_site"
                      value={formData.pessoa_site}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="https://www.exemplo.com"
                    />
                  </div>
                </div>
              </div>

              {/* Dados do Serviço */}
              <div className="form-section">
                <h4 className="form-section-title">Informações do Serviço</h4>
                <div className="form-grid form-grid-2">
                  <div className="form-field">
                    <label className="form-label form-label-required">Categoria</label>
                    <select
                      name="pessoa_categoria_fornecedor"
                      value={formData.pessoa_categoria_fornecedor}
                      onChange={handleInputChange}
                      required
                      className="form-select"
                    >
                      <option value="">Selecione uma categoria</option>
                      {categorias.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-label">Horário de Atendimento</label>
                    <input
                      type="text"
                      name="pessoa_horario_atendimento"
                      value={formData.pessoa_horario_atendimento}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Ex: Seg-Sex 8h às 18h"
                    />
                  </div>

                  <div className="form-field form-field-full">
                    <label className="form-label">Serviços Oferecidos</label>
                    <textarea
                      name="pessoa_servicos_oferecidos"
                      value={formData.pessoa_servicos_oferecidos}
                      onChange={handleInputChange}
                      rows="3"
                      className="form-textarea"
                      placeholder="Descreva os serviços oferecidos..."
                    />
                  </div>

                  <div className="form-field form-field-full">
                    <label className="form-label">Observações</label>
                    <textarea
                      name="pessoa_observacoes"
                      value={formData.pessoa_observacoes}
                      onChange={handleInputChange}
                      rows="3"
                      className="form-textarea"
                      placeholder="Digite observações sobre o fornecedor..."
                    />
                  </div>
                </div>
              </div>

              <div className="form-field form-field-full">
                <label className="form-label" style={{marginBottom: 0}}>
                  <input
                    type="checkbox"
                    name="pessoa_ativo"
                    checked={formData.pessoa_ativo}
                    onChange={handleInputChange}
                    className="form-checkbox"
                  />
                  {' '}Fornecedor Ativo
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingFornecedor(null);
                    resetForm();
                  }}
                  className="btn-base btn-secondary-custom"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-base btn-primary-custom btn-icon"
                >
                  <Save className="h-4 w-4" />
                  {isSubmitting ? 'Salvando...' : (editingFornecedor ? 'Atualizar' : 'Criar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fornecedores;

